import { Extension } from '@tiptap/core';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import type { Extensions } from '@tiptap/react';
import { DefaultEditorOptions } from 'ra-input-rich-text';

const styleAttribute = {
    default: null,
    parseHTML: (element: HTMLElement) => element.getAttribute('style'),
    renderHTML: (attributes: { style?: string | null }) => {
        if (!attributes.style) return {};
        return { style: attributes.style };
    },
};

/** Preserve inline style="" on common email HTML nodes. */
export const EmailInlineStyle = Extension.create({
    name: 'emailInlineStyle',
    addGlobalAttributes() {
        return [
            {
                types: [
                    'paragraph',
                    'heading',
                    'bulletList',
                    'orderedList',
                    'listItem',
                    'blockquote',
                    'image',
                    'horizontalRule',
                ],
                attributes: {
                    style: styleAttribute,
                },
            },
        ];
    },
});

// ponytail: default Image drops width/height/style; email HTML needs them preserved.
const EmailImage = Image.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            width: {
                default: null,
                parseHTML: (element: HTMLElement) => element.getAttribute('width'),
                renderHTML: (attributes: { width?: string | null }) => {
                    if (!attributes.width) return {};
                    return { width: attributes.width };
                },
            },
            height: {
                default: null,
                parseHTML: (element: HTMLElement) => element.getAttribute('height'),
                renderHTML: (attributes: { height?: string | null }) => {
                    if (!attributes.height) return {};
                    return { height: attributes.height };
                },
            },
            alt: {
                default: null,
                parseHTML: (element: HTMLElement) => element.getAttribute('alt'),
                renderHTML: (attributes: { alt?: string | null }) => {
                    if (!attributes.alt) return {};
                    return { alt: attributes.alt };
                },
            },
        };
    },
});

const EmailLink = Link.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            style: styleAttribute,
        };
    },
});

export function buildEmailEditorExtensions(): Extensions {
    const base = DefaultEditorOptions.extensions ?? [];
    return [
        ...base.map(ext => {
            if (ext.name === 'image') return EmailImage.configure({ inline: true });
            if (ext.name === 'link') return EmailLink;
            return ext;
        }),
        EmailInlineStyle,
    ];
}

export const UPLOADED_IMAGE_WIDTH = '400';
export const UPLOADED_IMAGE_STYLE = 'display: block; max-width: 100%; height: auto;';
